import { usePermissions } from '@refinedev/core'
import { Breadcrumb, Card, Space, Tabs } from 'antd'
import { Link, useParams } from 'react-router-dom'

import { gpuAdminConfigured } from '@/api/gpu/client'
import { hasPermission } from '@/auth/capabilities'
import { RecordFallback } from '@/components/common/RecordFallback'

import { BalanceDetails } from './components/Balance'
import {
  CountryExclusionsTab,
  OfferExclusionsTab,
} from './components/ExclusionTabs'
import { GpuState } from './components/GpuState'
import { HistoryTable } from './components/HistoryTable'
import { OffersTable } from './components/OffersTable'
import { ProviderDetails } from './components/ProviderDetails'
import { StatsCard } from './components/StatsCard'
import { useProviderControls } from './components/useProviderControls'
import { providerName } from './format'
import { useGpuProvider } from './hooks/useGpuAdmin'

export default function GpuProviderShowPage() {
  const { key = '' } = useParams<{ key: string }>()
  const { provider, isLoading, error } = useGpuProvider(key)
  const { data: permissions = [] } = usePermissions<string[]>({})
  const canWrite = hasPermission(permissions, 'admin:gpu:write')
  const { controls, markupModal } = useProviderControls()

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: 'GPU' },
          { title: <Link to="/gpu/providers">Providers</Link> },
          { title: providerName(key) },
        ]}
      />
      <GpuState error={error} />
      {gpuAdminConfigured && !provider && !error && (
        <RecordFallback
          label="GPU provider"
          isLoading={isLoading}
          error={null}
        />
      )}
      {provider && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card size="small" title={providerName(provider.key)}>
            <ProviderDetails
              provider={provider}
              controls={canWrite ? controls : undefined}
            />
          </Card>
          <Card size="small" title="Account balance">
            <BalanceDetails balance={provider.balance} />
          </Card>
          <StatsCard provider={provider.key} />
          <Card size="small">
            <Tabs
              items={[
                {
                  key: 'offers',
                  label: 'Offers',
                  children: (
                    <OffersTable provider={provider.key} canWrite={canWrite} />
                  ),
                },
                {
                  key: 'excluded-offers',
                  label: 'Taken off sale',
                  children: (
                    <OfferExclusionsTab
                      provider={provider.key}
                      canWrite={canWrite}
                    />
                  ),
                },
                // Hidden where the module never reports countries.
                ...(provider.countryExclusion.supported
                  ? [
                      {
                        key: 'excluded-countries',
                        label: 'Excluded countries',
                        children: (
                          <CountryExclusionsTab
                            provider={provider.key}
                            canWrite={canWrite}
                          />
                        ),
                      },
                    ]
                  : []),
                {
                  key: 'history',
                  label: 'Settings history',
                  children: <HistoryTable provider={provider.key} />,
                },
              ]}
            />
          </Card>
        </Space>
      )}
      {markupModal}
    </div>
  )
}
